import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminSystemMessagesService } from '../services/adminSystemMessagesService';
import { formatDate, formatTime } from '../utils/dateUtils';

export const useAdminSystemMessages = (initialFilters = {}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    type: '',
    is_read: '',
    date_from: '',
    date_to: '',
    search: '',
    sort_by: 'created_at',
    sort_order: 'desc',
    per_page: 15,
    ...initialFilters
  });

  const typeOptions = [
    { value: '', label: 'All Types' },
    { value: 'booking_placed', label: 'Booking Placed' },
    { value: 'payment_received', label: 'Payment Received' },
    { value: 'user_registered', label: 'User Registered' },
    { value: 'vendor_subscribed', label: 'Vendor Subscribed' },
  ];

  const readStatusOptions = [
    { value: '', label: 'All Messages' },
    { value: 'true', label: 'Read' },
    { value: 'false', label: 'Unread' }
  ];

  const cleanFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
  }, [filters]);

  const queryString = new URLSearchParams(cleanFilters).toString();

  // Fetch messages
  const { data: messagesResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['adminSystemMessages', cleanFilters],
    queryFn: async () => {
      return await adminSystemMessagesService.getAll(cleanFilters);
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  // Fetch statistics
  const { data: statisticsResponse } = useQuery({
    queryKey: ['adminSystemMessagesStatistics'],
    queryFn: async () => {
      return await adminSystemMessagesService.getStatistics();
    },
    refetchOnWindowFocus: true,
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (id) => adminSystemMessagesService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminSystemMessages']);
      queryClient.invalidateQueries(['adminSystemMessagesStatistics']);
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: () => adminSystemMessagesService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminSystemMessages']);
      queryClient.invalidateQueries(['adminSystemMessagesStatistics']);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => adminSystemMessagesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminSystemMessages']);
      queryClient.invalidateQueries(['adminSystemMessagesStatistics']);
    },
  });

  const messages = useMemo(() => {
    return messagesResponse?.data?.data || [];
  }, [messagesResponse]);

  const pagination = useMemo(() => {
    return messagesResponse?.data || {};
  }, [messagesResponse]);

  const statistics = useMemo(() => {
    return statisticsResponse?.data || {};
  }, [statisticsResponse]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    const term = searchTerm.toLowerCase();
    return data.filter(item => 
      item.title?.toLowerCase().includes(term) ||
      item.message?.toLowerCase().includes(term) ||
      item.type?.toLowerCase().includes(term)
    );
  };

  const markAsRead = async (id) => {
    try {
      await markAsReadMutation.mutateAsync(id);
    } catch (error) {
      console.error('Failed to mark message as read:', error);
      throw error;
    }
  };

  const markAllAsRead = async () => {
    try {
      await markAllAsReadMutation.mutateAsync();
    } catch (error) {
      console.error('Failed to mark all messages as read:', error);
      throw error;
    }
  };

  const deleteMessage = async (id) => {
    try {
      await deleteMutation.mutateAsync(id);
    } catch (error) {
      console.error('Failed to delete message:', error);
      throw error;
    }
  };

  return {
    messages,
    pagination,
    statistics,
    isLoading,
    error,
    filters,
    typeOptions,
    readStatusOptions,
    handleFilterChange,
    handleSearch,
    markAsRead,
    markAllAsRead,
    deleteMessage,
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
    isDeleting: deleteMutation.isPending,
    refetch,
  };
};

